<script setup>
import { ref, watch, computed, defineProps, defineEmits } from "vue";

const emit = defineEmits(["update:modelValue", "continue"]);

const props = defineProps({
  modelValue: {
    type: String,
    default: "",
  },
});

const selectedMode = ref(props.modelValue);

const distributionDetails = computed(() => {
  if (selectedMode.value === "open_source") {
    return {
      title: "Open Source",
      subtitle: "Share your eval publicly with no commercial pricing.",
      bestFor:
        "Great when your goal is community adoption, transparency, and public benchmarking.",
      bullets: [
        { text: "No pricing or payment setup in this flow." },
        { text: "Commercial-only fields are hidden in listing details." },
        {
          text: "Publishes to a public GitHub repository:",
          href: "https://github.com/portex-ai/tasks",
          linkLabel: "portex-ai/tasks",
        },
        {
          text: "Tasks are open sourced to comply with the Harbor Framework:",
          href: "https://harborframework.com/",
          linkLabel: "Harbor Framework",
        },
      ],
      cta: "Continue to Open Source Details",
    };
  }

  return {
    title: "Commercial License",
    subtitle: "Commercialize your eval with pricing and licensing controls.",
    bestFor:
      "Best when you want to sell eval access and control terms for buyers.",
    bullets: [
      { text: "Set eval and core dataset pricing." },
      { text: "Enable Stripe and/or USDC payment methods." },
      { text: "Configure and attach your commercial license." },
    ],
    cta: "Continue to Commercial Details",
  };
});

watch(
  () => props.modelValue,
  (newValue) => {
    selectedMode.value = newValue;
  }
);

const handleModeSelection = (mode) => {
  selectedMode.value = mode;
  emit("update:modelValue", mode);
};

const handleContinue = () => {
  if (!selectedMode.value) return;
  emit("continue");
};
</script>

<template>
  <div class="eval-entrypoint-shell">
    <div class="eval-entrypoint-left gradient-divider">
      <div class="eval-distribution-header">
        <div class="eval-distribution-title">Choose your publishing model</div>
        <div class="eval-distribution-subtitle">
          Select a model now. The listing form will update based on your choice.
        </div>
      </div>
      <div class="grid grid-cols-1 gap-4 mt-4">
        <button
          type="button"
          class="dataset-type-card"
          :class="selectedMode === 'commercial' ? 'active' : 'inactive'"
          @click="handleModeSelection('commercial')"
        >
          <i
            class="uil uil-money-withdraw dataset-type-card-icon"
            :class="selectedMode === 'commercial' ? 'active' : ''"
          ></i>
          <div>
            <div class="dataset-type-card-title">Commercial License</div>
            <div class="dataset-type-card-description">
              Sell access to your eval with pricing, payment methods, and
              license configuration.
            </div>
          </div>
        </button>
        <button
          type="button"
          class="dataset-type-card"
          :class="
            selectedMode === 'open_source' ? 'active qa-active' : 'inactive'
          "
          @click="handleModeSelection('open_source')"
        >
          <i
            class="uil uil-book-open dataset-type-card-icon"
            :class="selectedMode === 'open_source' ? 'active qa-active' : ''"
          ></i>
          <div>
            <div class="dataset-type-card-title">Open Source</div>
            <div class="dataset-type-card-description">
              Share your eval for free. Commercial-only fields are hidden in the
              next step.
            </div>
          </div>
        </button>
      </div>
    </div>

    <div class="eval-entrypoint-right">
      <div class="flex flex-col items-center justify-center mb-5">
        <h1 class="distribution-title">{{ distributionDetails.title }}</h1>
        <p class="text-sm 2xl:text-base text-gray-300 text-center mt-1">
          {{ distributionDetails.subtitle }}
        </p>
      </div>
      <div class="space-y-5">
        <div class="detail-card">
          <h3 class="detail-card-title">Best For</h3>
          <p class="detail-card-text">{{ distributionDetails.bestFor }}</p>
        </div>
        <div class="detail-card">
          <h3 class="detail-card-title">What This Means</h3>
          <ul
            class="list-disc pl-5 space-y-2 text-sm 2xl:text-base text-white/85"
          >
            <li
              v-for="bullet in distributionDetails.bullets"
              :key="bullet.text"
            >
              {{ bullet.text }}
              <a
                v-if="bullet.href"
                :href="bullet.href"
                target="_blank"
                rel="noopener noreferrer"
                class="text-echoes-primary hover:text-echoes-primary/80 underline ml-1"
              >
                {{ bullet.linkLabel || bullet.href }}
              </a>
            </li>
          </ul>
        </div>
      </div>
      <div class="flex justify-end mt-6">
        <button
          class="continue-btn"
          data-testid="submit-pre-listing-button"
          @click="handleContinue"
          :disabled="!selectedMode"
        >
          <span class="text-sm">{{ distributionDetails.cta }}</span>
          <span class="tooltip" v-if="!selectedMode">Select a publishing model to continue</span>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="sass">
.eval-entrypoint-shell {
    @apply w-full flex flex-col lg:flex-row items-start pb-4 lg:pb-0;
}

.eval-entrypoint-left {
    @apply w-full lg:w-[40%] lg:pr-10 pb-6 lg:pb-0;
}

.eval-entrypoint-right {
    @apply w-full lg:flex-1 lg:pl-10 pb-3;
}

.eval-distribution-header {
    @apply space-y-1;
}

.eval-distribution-title {
    @apply text-white text-sm md:text-base font-semibold;
}

.eval-distribution-subtitle {
    @apply text-white/70 text-xs md:text-sm;
}

.dataset-type-card {
    @apply flex items-center rounded-2xl border py-5 2xl:py-6 px-3 bg-echoes-background-gray/50 cursor-pointer transition-all duration-300 gap-3 text-left;
}

.dataset-type-card:hover {
    @apply bg-echoes-background-gray/70;
}

.dataset-type-card.inactive {
    @apply border-echoes-border-gray;
}

.dataset-type-card.active {
    @apply border-echoes-primary;
}

.dataset-type-card.active.qa-active {
    border-color: rgb(74 222 128 / 0.8);
}

.dataset-type-card-icon {
    @apply text-4xl flex items-center justify-center w-10 flex-shrink-0 text-white/70;
}

.dataset-type-card-icon.active {
    @apply text-echoes-primary;
}

.dataset-type-card-icon.active.qa-active {
    color: rgb(134 239 172 / 1);
}

.dataset-type-card-title {
    @apply text-base 2xl:text-lg font-medium text-white;
}

.dataset-type-card-description {
    @apply text-sm text-gray-400;
}

.detail-card {
    @apply rounded-xl border border-echoes-border-gray bg-echoes-background-black/60 p-4;
}

.detail-card-title {
    @apply text-sm uppercase tracking-wide text-white/70 mb-2 font-semibold;
}

.detail-card-text {
    @apply text-sm text-white/90;
}

.distribution-title {
    @apply text-2xl 2xl:text-3xl font-bold mb-3 bg-gradient-to-r from-[#FFFFFF] to-[#999999] bg-clip-text text-transparent w-fit;
}

.continue-btn {
    @apply px-6 2xl:px-8 py-2 text-sm 2xl:text-base bg-gradient-to-br from-green-500 to-green-700 text-white font-semibold rounded-full hover:bg-gradient-to-br hover:from-green-600 hover:to-green-800 transition duration-200 flex items-center justify-center min-w-[160px] disabled:opacity-70 disabled:cursor-not-allowed;
    position: relative;
}

.gradient-divider {
    position: relative;
}

.gradient-divider::after {
    content: '';
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: 2px;
    background: linear-gradient(to top, transparent 0%, #232323 100%);
}

@media (max-width: 1023px) {
    .gradient-divider::after {
        display: none;
    }
}
</style>
